/**
 * Game session storage constants and utilities.
 * This module owns the localStorage schema for game session state.
 */

export const GAME_STATE_KEY = 'trivia_game_state';
export const GAME_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

export interface StoredGameSession {
  id: string;
  lastActiveAt: string;
  guestTotalXP: number;
  leaderboardScope?: 'friends' | 'board' | 'all';
}

/** Restore the owning visit atomically before descendants choose their first scope. */
export function readActiveGameSession(): StoredGameSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const session: StoredGameSession | null = JSON.parse(
      localStorage.getItem(GAME_STATE_KEY) ?? 'null'
    );
    if (!session || typeof session.id !== 'string' || typeof session.lastActiveAt !== 'string')
      return null;
    const inactiveMs = Date.now() - Date.parse(session.lastActiveAt);
    return Number.isFinite(inactiveMs) && inactiveMs <= GAME_SESSION_TIMEOUT_MS
      ? {
          ...session,
          guestTotalXP: Number.isFinite(session.guestTotalXP) ? session.guestTotalXP : 0,
        }
      : null;
  } catch {
    return null;
  }
}

/**
 * Clear guest XP from the unified game session storage.
 * Preserves the session ID and lastActiveAt, only resets XP.
 */
export function clearGuestXPFromSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const stored = localStorage.getItem(GAME_STATE_KEY);
    if (!stored) return;
    try {
      const session: StoredGameSession = JSON.parse(stored);
      session.guestTotalXP = 0;
      localStorage.setItem(GAME_STATE_KEY, JSON.stringify(session));
    } catch {
      // If parsing fails, don't modify storage
    }
  } catch {
    // LocalStorage blocked
  }
}
